import {
  candidateToSymbol, customToDef, decodeDxf, parseDxf, validateCustomSymbol, CUSTOM_CAT, type CustomSymbol, type DxfFile, type Prim,
} from '@enercad/core';
import { api, type User } from './api';
import { esc, openModal, qs, toast } from './ui';
import { t as $t, tk } from '@enercad/core';


/* Symbol editor: draw user symbols from lines, rectangles, circles, texts and pins. */

type Draft = Omit<CustomSymbol, 'key'> & { key?: string };
type Tool = 'sel' | 'line' | 'rect' | 'circle' | 'text' | 'pin';
type SelRef = { t: 'prim' | 'pin'; i: number } | null;

const TOOLS: [Tool, string, string][] = [
  ['sel', tk("Seç"), tk("Tıkla: seç · Sürükle: taşı · Del: sil")],
  ['line', tk("Çizgi"), tk("Noktalara tıkla; çift tık, Enter ya da sağ tık bitirir")],
  ['rect', tk("Dikdörtgen"), tk("Bir köşeden karşı köşeye sürükle")],
  ['circle', tk('Daire'), tk("Merkezden dışa doğru sürükle")],
  ['text', tk('Metin'), tk("Metnin ortasına tıkla, sonra sağ panelden yaz")],
  ['pin', tk("Bağlantı noktası"), tk("Telin bağlanacağı yere tıkla (ızgaraya oturur)")],
];

const blank = (): Draft => ({
  name: $t("Yeni sembol"), cat: CUSTOM_CAT, prefix: 'U', role: 'main', mirror: false,
  pins: [{ x: 0, y: 0, label: '1' }, { x: 0, y: 16, label: '2' }],
  prims: [{ k: 'line', pts: [[0, 0], [0, 4]] }, { k: 'rect', x: -4, y: 4, w: 8, h: 8 }, { k: 'line', pts: [[0, 12], [0, 16]] }],
});

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v));

function previewSvg(d: Draft, w = 64, h = 48) {
  try {
    const def = customToDef(validateCustomSymbol({ ...d, key: d.key ?? 'u_preview' }));
    const b = def.bb, pad = 3, vw = b[2] - b[0] + pad * 2, vh = b[3] - b[1] + pad * 2;
    return `<svg viewBox="${b[0] - pad} ${b[1] - pad} ${vw} ${vh}" width="${w}" height="${h}" aria-hidden="true"><g class="sym">${def.svg}</g></svg>`;
  } catch {
    return `<svg width="${w}" height="${h}"></svg>`;
  }
}

export async function symbolsView(root: HTMLElement, me: User, topbar: string, bindTop: () => void, reload: (force: boolean) => Promise<void>) {
  root.innerHTML = `<div class="shell">${topbar}
  <div class="symed">
    <aside class="left"><div class="sec"><h3>${$t("Kullanıcı sembolleri")} <button class="btn" id="new">${$t("+ Yeni")}</button></h3>
      <button class="btn" id="dxf" style="width:100%;justify-content:center;margin-bottom:10px">${$t("DXF'ten içe aktar…")}</button>
      <input type="file" id="dxfFile" accept=".dxf" multiple hidden><div id="slist"></div>
      <p class="note">${$t("Kaydedilen semboller bu sunucudaki herkesin sembol kütüphanesinde \"{0}\" altında görünür. Kullanıldıkları projelerin içine de kopyalanır; proje başka bir sunucuya taşınsa da açılır.", $t(CUSTOM_CAT))}</p></div></aside>
    <main class="stage">
      <div class="symtools">${TOOLS.map(([t, n]) => `<button class="btn" data-tool="${t}">${$t(n)}</button>`).join('')}
        <span class="spacer"></span><label class="chkline" style="margin:0"><input type="checkbox" id="half"> ${$t("0,5 mm ızgara")}</label>
        <button class="btn" id="undo">${$t("↶ Geri")}</button><button class="btn" id="zout">−</button><button class="btn" id="zin">+</button></div>
      <svg class="cv symcv" id="cv" xmlns="http://www.w3.org/2000/svg">
        <defs><pattern id="g1" width="1" height="1" patternUnits="userSpaceOnUse"><circle cx="0" cy="0" r=".06" class="gdot"/></pattern>
        <pattern id="g4" width="4" height="4" patternUnits="userSpaceOnUse"><path d="M4 0H0V4" fill="none" stroke="var(--gdot)" stroke-width=".05"/></pattern></defs>
        <g id="grid"></g><g id="body"></g><g id="ov"></g></svg>
      <div class="status"><span id="pos">X — Y —</span><span id="hint"></span></div>
    </main>
    <aside class="right" id="sprops"></aside>
  </div></div>`;
  bindTop();

  const svg = qs<SVGSVGElement>(root, '#cv');
  let list: (CustomSymbol & { createdBy: number; shared?: boolean; editable?: boolean })[] = [];
  let d: Draft = blank();
  let tool: Tool = 'sel', sel: SelRef = null, draft: [number, number][] = [], drag: any = null, mouse: [number, number] = [0, 0];
  let span = 56;
  const hist: string[] = [];
  const push = () => {
    hist.push(JSON.stringify(d));
    if (hist.length > 100) hist.shift();
  };
  const step = () => (qs<HTMLInputElement>(root, '#half').checked ? 0.5 : 1);
  const snap = (v: number) => Math.round(v / step()) * step();
  const toW = (e: MouseEvent): [number, number] => {
    const p = svg.createSVGPoint();
    p.x = e.clientX;
    p.y = e.clientY;
    const q = p.matrixTransform(svg.getScreenCTM()!.inverse());
    return [q.x, q.y];
  };

  const loadList = async () => {
    list = (await api.symbols()).symbols;
    qs(root, '#slist').innerHTML =
      list.map((s) => `<button class="symitem${d.key === s.key ? ' on' : ''}" data-key="${esc(s.key)}">${previewSvg(s, 40, 30)}<span>${esc(s.name)}${s.shared ? ` <span class="pill ok">${$t("ortak")}</span>` : ''}<br><span class="note">-${esc(s.prefix)} · ${esc(roleName(s.role))}</span></span></button>`).join('') ||
      `<p class="note">${$t("Henüz sembol yok.")}</p>`;
  };
  /** Shared symbols are read-only for companies (the server says which ones this user may edit). */
  const readOnlySym = () => !!d.key && list.find((x) => x.key === d.key)?.editable === false;
  const roleName = (r: string) => ({ main: 'ana fonksiyon', aux: 'kontak', plc: 'PLC', term: 'klemens' })[r] ?? r;

  const render = () => {
    const h = span * 0.8;
    svg.setAttribute('viewBox', `${-span / 2} ${-h / 3} ${span} ${h}`);
    qs(root, '#grid').innerHTML = `<rect x="-200" y="-200" width="400" height="400" fill="var(--paper)"/><rect x="-200" y="-200" width="400" height="400" fill="url(#g4)"/>${span < 90 ? '<rect x="-200" y="-200" width="400" height="400" fill="url(#g1)"/>' : ''}<path d="M-2 0H2M0 -2V2" stroke="var(--accent)" stroke-width=".12"/>`;
    let o = '';
    d.prims.forEach((p, i) => {
      const on = sel?.t === 'prim' && sel.i === i ? ' on' : '';
      if (p.k === 'line') o += `<polyline class="sp${on}" data-i="${i}" points="${p.pts.map((q) => q.join(',')).join(' ')}"${p.dash ? ' stroke-dasharray="1.1 .9"' : ''}/>`;
      else if (p.k === 'rect') o += `<rect class="sp${on}${p.fill ? ' f' : ''}" data-i="${i}" x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}"/>`;
      else if (p.k === 'circle') o += `<circle class="sp${on}${p.fill ? ' f' : ''}" data-i="${i}" cx="${p.x}" cy="${p.y}" r="${p.r}"/>`;
      else o += `<text class="st${on}" data-i="${i}" x="${p.x}" y="${p.y}" font-size="${p.size}" text-anchor="middle">${esc(p.text)}</text>`;
    });
    d.pins.forEach((p, i) => {
      const on = sel?.t === 'pin' && sel.i === i ? ' on' : '';
      o += `<g class="pin${on}" data-p="${i}"><circle cx="${p.x}" cy="${p.y}" r=".9"/><text x="${p.x + 1.4}" y="${p.y - 0.8}" font-size="1.8">${esc(p.label || String(i + 1))}</text></g>`;
    });
    qs(root, '#body').innerHTML = o;
    renderOv();
    root.querySelectorAll<HTMLElement>('[data-tool]').forEach((b) => b.classList.toggle('on', b.dataset.tool === tool));
    qs(root, '#hint').textContent = $t(TOOLS.find((t) => t[0] === tool)![2]);
    renderProps();
  };
  const renderOv = () => {
    const m = [snap(mouse[0]), snap(mouse[1])];
    let o = tool !== 'sel' ? `<path d="M${m[0] - 0.8} ${m[1]}h1.6M${m[0]} ${m[1] - 0.8}v1.6" stroke="var(--accent)" stroke-width=".1"/>` : '';
    if (tool === 'line' && draft.length) o += `<polyline class="sp draftl" points="${[...draft, m].map((q) => q.join(',')).join(' ')}"/>`;
    if (drag?.k === 'rect') {
      const [a, b] = [drag.a, m];
      o += `<rect class="sp draftl" x="${Math.min(a[0], b[0])}" y="${Math.min(a[1], b[1])}" width="${Math.abs(a[0] - b[0])}" height="${Math.abs(a[1] - b[1])}"/>`;
    }
    if (drag?.k === 'circle') o += `<circle class="sp draftl" cx="${drag.a[0]}" cy="${drag.a[1]}" r="${Math.hypot(m[0] - drag.a[0], m[1] - drag.a[1])}"/>`;
    qs(root, '#ov').innerHTML = o;
  };

  const f = (id: string, label: string, v: unknown, extra = '') => `<label class="fld"><span>${label}</span><input id="${id}" value="${esc(v ?? '')}" ${extra}></label>`;
  const renderProps = () => {
    let o = `<div class="sec"><div class="symhead">${previewSvg(d, 52, 40)}<div><b>${esc(d.name)}</b><span>${d.key ? esc(d.key) : 'kaydedilmedi'}</span></div></div>
      ${f('p_name', 'Ad', d.name)}
      <div class="row2">${f('p_prefix', $t("Etiket öneki"), d.prefix, 'class="tag" maxlength="4"')}${f('p_cat', 'Kategori', d.cat)}</div>
      <label class="fld"><span>${$t("Rol")}</span><select id="p_role">${(['main', 'aux', 'plc', 'term'] as const).map((r) => `<option value="${r}"${d.role === r ? ' selected' : ''}>${roleName(r)}</option>`).join('')}</select></label>
      ${d.role === 'aux' ? `<label class="fld"><span>${$t("Kontak türü")}</span><select id="p_kind"><option value="no"${d.kind !== 'nc' ? ' selected' : ''}>${$t("Kapanan (NO)")}</option><option value="nc"${d.kind === 'nc' ? ' selected' : ''}>${$t("Açan (NC)")}</option></select></label>` : ''}
      ${d.role === 'main' ? `<label class="chkline"><input type="checkbox" id="p_mirror"${d.mirror ? ' checked' : ''}> ${$t("Kontak görüntüsünü yanında göster (bobin, şalter gibi)")}</label>` : ''}
      <p class="note">${d.role === 'aux' || d.role === 'term' ? $t("Bağlantı noktalarının ilk yarısı ikinci yarısına bağlı sayılır; potansiyel kontaktan geçer.") : $t("Ana fonksiyon: aynı etiketli kontaklar bu sembole çapraz referans verir.")}</p>
      ${!d.key && me.platform_admin ? `<label class="chkline"><input type="checkbox" id="toShared"> ${$t("Ortak kütüphaneye kaydet (tüm firmalar görür)")}</label>` : ''}
      ${readOnlySym()
        ? `<p class="note">${$t("Bu sembol ortak kütüphaneden; değiştirmek için firmanıza kopyasını oluşturun.")}</p><div class="btnrow"><button class="btn pri" id="copy">${$t("Kopyasını oluştur")}</button></div>`
        : `<div class="btnrow"><button class="btn pri" id="save">${$t("Kaydet")}</button>${d.key ? `<button class="btn danger" id="del">${$t("Sil")}</button>` : ''}</div>`}</div>`;
    if (sel) {
      o += `<div class="sec"><h3>${$t("Seçili öğe")}</h3>`;
      if (sel.t === 'pin') {
        const p = d.pins[sel.i];
        o += `${f('e_label', $t("Bağlantı noktası adı"), p.label, 'class="tag" maxlength="12"')}<dl class="kv"><dt>${$t("Konum")}</dt><dd>${p.x}, ${p.y}</dd><dt>${$t("Sıra")}</dt><dd>${sel.i + 1} / ${d.pins.length}</dd></dl>`;
      } else {
        const p = d.prims[sel.i];
        if (p.k === 'text') o += f('e_text', 'Metin', p.text, 'maxlength="40"') + f('e_size', $t("Yazı yüksekliği mm"), p.size, 'inputmode="decimal"');
        if (p.k === 'line') o += `<label class="chkline"><input type="checkbox" id="e_dash"${p.dash ? ' checked' : ''}> ${$t("Kesik çizgi (mekanik bağlantı)")}</label>`;
        if (p.k === 'rect' || p.k === 'circle') o += `<label class="chkline"><input type="checkbox" id="e_fill"${p.fill ? ' checked' : ''}> ${$t("Dolu")}</label>`;
      }
      o += `<div class="btnrow"><button class="btn danger" id="edel">${$t("Öğeyi sil")}</button></div></div>`;
    }
    o += `<div class="sec"><h3>${$t("Bağlantı noktaları")}</h3><ul class="conn">${d.pins.map((p, i) => `<li>${i + 1}. ${esc(p.label || '—')} · ${p.x}, ${p.y}</li>`).join('') || `<li>${$t("Yok — \"Bağlantı noktası\" aracıyla ekleyin")}</li>`}</ul></div>`;
    qs(root, '#sprops').innerHTML = o;
  };

  const hitAt = (e: Event): SelRef => {
    const t = e.target as Element;
    const pi = t.closest('[data-p]')?.getAttribute('data-p');
    if (pi != null) return { t: 'pin', i: +pi };
    const i = t.closest('[data-i]')?.getAttribute('data-i');
    return i != null ? { t: 'prim', i: +i } : null;
  };
  const finishLine = () => {
    if (draft.length >= 2) {
      push();
      d.prims.push({ k: 'line', pts: draft });
      sel = { t: 'prim', i: d.prims.length - 1 };
    }
    draft = [];
    render();
  };
  const movePrim = (p: Prim, dx: number, dy: number): Prim => {
    if (p.k === 'line') return { ...p, pts: p.pts.map((q) => [q[0] + dx, q[1] + dy] as [number, number]) };
    return { ...p, x: p.x + dx, y: p.y + dy } as Prim;
  };

  svg.addEventListener('contextmenu', (e) => e.preventDefault());
  svg.addEventListener('pointerdown', (e) => {
    const w = toW(e), p: [number, number] = [snap(w[0]), snap(w[1])];
    if (e.button === 2) {
      if (tool === 'line') finishLine();
      return;
    }
    if (tool === 'line') {
      draft.push(p);
      return renderOv();
    }
    if (tool === 'rect' || tool === 'circle') {
      drag = { k: tool, a: p };
      svg.setPointerCapture(e.pointerId);
      return;
    }
    if (tool === 'text') {
      push();
      d.prims.push({ k: 'text', x: p[0], y: p[1], size: 2.5, text: 'ABC' });
      sel = { t: 'prim', i: d.prims.length - 1 };
      tool = 'sel';
      render();
      qs<HTMLInputElement>(root, '#e_text').select();
      return;
    }
    if (tool === 'pin') {
      push();
      d.pins.push({ x: Math.round(w[0] / 4) * 4, y: Math.round(w[1] / 4) * 4, label: String(d.pins.length + 1) });
      sel = { t: 'pin', i: d.pins.length - 1 };
      return render();
    }
    sel = hitAt(e);
    if (sel) {
      drag = { k: 'move', a: p, snap: JSON.stringify(d), orig: clone(sel.t === 'pin' ? d.pins[sel.i] : d.prims[sel.i]), moved: false };
      svg.setPointerCapture(e.pointerId);
    }
    render();
  });
  svg.addEventListener('pointermove', (e) => {
    mouse = toW(e);
    qs(root, '#pos').textContent = `X ${snap(mouse[0])} · Y ${snap(mouse[1])} mm`;
    if (drag?.k === 'move' && sel) {
      const pinStep = sel.t === 'pin' ? 4 : step();
      const dx = Math.round((mouse[0] - drag.a[0]) / pinStep) * pinStep, dy = Math.round((mouse[1] - drag.a[1]) / pinStep) * pinStep;
      if (dx || dy) drag.moved = true;
      if (sel.t === 'pin') d.pins[sel.i] = { ...drag.orig, x: drag.orig.x + dx, y: drag.orig.y + dy };
      else d.prims[sel.i] = movePrim(drag.orig, dx, dy);
      render();
    } else renderOv();
  });
  svg.addEventListener('pointerup', () => {
    const m: [number, number] = [snap(mouse[0]), snap(mouse[1])];
    if (drag?.k === 'rect') {
      const [a, b] = [drag.a, m], w = Math.abs(a[0] - b[0]), h = Math.abs(a[1] - b[1]);
      if (w && h) {
        push();
        d.prims.push({ k: 'rect', x: Math.min(a[0], b[0]), y: Math.min(a[1], b[1]), w, h });
      }
    } else if (drag?.k === 'circle') {
      const r = Math.round(Math.hypot(m[0] - drag.a[0], m[1] - drag.a[1]) / step()) * step();
      if (r) {
        push();
        d.prims.push({ k: 'circle', x: drag.a[0], y: drag.a[1], r });
      }
    } else if (drag?.k === 'move' && drag.moved) {
      hist.push(drag.snap);
    }
    drag = null;
    render();
  });
  svg.addEventListener('dblclick', () => tool === 'line' && finishLine());
  svg.addEventListener('wheel', (e) => {
    e.preventDefault();
    span = Math.max(16, Math.min(240, span * (e.deltaY < 0 ? 0.87 : 1.15)));
    render();
  }, { passive: false });

  const onKey = (e: KeyboardEvent) => {
    if (!document.body.contains(svg)) return document.removeEventListener('keydown', onKey);
    if (document.querySelector('.modal') || /INPUT|TEXTAREA|SELECT/.test((document.activeElement as HTMLElement)?.tagName)) return;
    if (e.key === 'Enter' && tool === 'line') finishLine();
    else if (e.key === 'Escape') {
      draft = [];
      tool = 'sel';
      render();
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && sel) {
      e.preventDefault();
      delSel();
    } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      undo();
    }
  };
  document.addEventListener('keydown', onKey);
  const delSel = () => {
    if (!sel) return;
    push();
    if (sel.t === 'pin') d.pins.splice(sel.i, 1);
    else d.prims.splice(sel.i, 1);
    sel = null;
    render();
  };
  const undo = () => {
    const s = hist.pop();
    if (!s) return;
    d = JSON.parse(s);
    sel = null;
    render();
  };

  root.addEventListener('click', async (e) => {
    const t = e.target as HTMLElement;
    const tb = t.closest<HTMLElement>('[data-tool]')?.dataset.tool as Tool | undefined;
    if (tb) {
      if (tool === 'line') finishLine();
      tool = tb;
      return render();
    }
    const key = t.closest<HTMLElement>('[data-key]')?.dataset.key;
    if (key) {
      const s = list.find((x) => x.key === key);
      if (s) {
        d = clone(s);
        delete (d as any).createdBy;
        hist.length = 0;
        sel = null;
        await loadList();
        render();
      }
      return;
    }
    if (t.id === 'new') {
      d = blank();
      hist.length = 0;
      sel = null;
      await loadList();
      return render();
    }
    if (t.id === 'dxf') return qs<HTMLInputElement>(root, '#dxfFile').click();
    if (t.id === 'undo') return undo();
    if (t.id === 'zin' || t.id === 'zout') {
      span = Math.max(16, Math.min(240, span * (t.id === 'zin' ? 0.8 : 1.25)));
      return render();
    }
    if (t.id === 'edel') return delSel();
    if (t.id === 'copy') {
      d = { ...clone(d), key: undefined, name: $t("{0} (kopya)", d.name) };
      hist.length = 0;
      render();
      return toast($t("Kopya oluşturuldu; düzenleyip Kaydet deyin"));
    }
    if (t.id === 'save') {
      try {
        validateCustomSymbol({ ...d, key: d.key ?? 'u_check' });
        const r = await api.saveSymbol({ ...d, ...(!d.key && root.querySelector<HTMLInputElement>('#toShared')?.checked ? { shared: true } : {}) });
        d = clone(r.symbol);
        await reload(true);
        await loadList();
        toast($t('Sembol kaydedildi; kütüphanede "{0}" altında', $t(d.cat)));
        render();
      } catch (x) {
        toast((x as Error).message);
      }
      return;
    }
    if (t.id === 'del' && d.key) {
      const k = d.key;
      const close = openModal(
        `<h2>${$t("\"{0}\" silinsin mi?", esc(d.name))}</h2><p>${$t("Sembol kütüphaneden kalkar. Bu sembolü kullanan projeler etkilenmez; sembol tanımı projelerin içinde kayıtlıdır.")}</p>
        <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Sembolü sil")}</button></div>`,
        async (a) => {
          if (a !== 'ok') return;
          try {
            await api.deleteSymbol(k);
            close();
            d = blank();
            await reload(true);
            await loadList();
            render();
          } catch (x) {
            toast((x as Error).message);
          }
        },
      );
    }
  });
  root.addEventListener('change', (e) => {
    const t = e.target as HTMLInputElement;
    if (!t.id?.match(/^[pe]_/) || t.id === 'half') return;
    push();
    const v = t.value;
    if (t.id === 'p_name') d.name = v.slice(0, 60);
    if (t.id === 'p_prefix') d.prefix = v.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4) || 'U';
    if (t.id === 'p_cat') d.cat = v.slice(0, 40) || CUSTOM_CAT;
    if (t.id === 'p_role') d.role = v as Draft['role'];
    if (t.id === 'p_kind') d.kind = v === 'nc' ? 'nc' : 'no';
    if (t.id === 'p_mirror') d.mirror = t.checked;
    if (sel?.t === 'pin' && t.id === 'e_label') d.pins[sel.i].label = v.slice(0, 12);
    if (sel?.t === 'prim') {
      const p = d.prims[sel.i] as any;
      if (t.id === 'e_text') p.text = v.slice(0, 40);
      if (t.id === 'e_size') p.size = Math.min(20, Math.max(1, Number(v.replace(',', '.')) || 2.5));
      if (t.id === 'e_dash') p.dash = t.checked;
      if (t.id === 'e_fill') p.fill = t.checked;
    }
    render();
  });

  /* ---------- DXF import ---------- */
  const dxfInput = qs<HTMLInputElement>(root, '#dxfFile');
  dxfInput.onchange = async () => {
    const files = [...(dxfInput.files ?? [])];
    dxfInput.value = '';
    if (!files.length) return;
    // several files: one symbol per file is common (manufacturer libraries) → name candidates after the file
    const parsed: DxfFile = { version: '', units: '', candidates: [], warnings: [] };
    const failed: string[] = [];
    const versions = new Set<string>(), units = new Set<string>(), warns = new Set<string>();
    for (const f of files) {
      try {
        const r = parseDxf(decodeDxf(new Uint8Array(await f.arrayBuffer())));
        const fstem = f.name.replace(/\.dxf$/i, '').trim();
        for (const c of r.candidates) {
          if (files.length > 1) c.name = r.candidates.length === 1 ? fstem : `${fstem} · ${c.name}`;
          parsed.candidates.push(c);
        }
        versions.add(r.version);
        units.add(r.units);
        r.warnings.forEach((w) => warns.add(w));
      } catch (x) {
        failed.push(`${f.name}: ${(x as Error).message}`);
      }
    }
    parsed.version = [...versions].join(', ');
    parsed.units = [...units].join(', ');
    parsed.warnings = [...warns, ...(failed.length ? [$t("Okunamayan dosyalar: {0}", failed.join('; '))] : [])];
    const file = { name: files.length === 1 ? files[0].name : $t("{0} dosya", files.length) };
    if (!parsed.candidates.length && failed.length)
      return void openModal(`<h2>${$t("DXF okunamadı")}</h2><p>${esc(failed.join(' · '))}</p><div class="acts"><button class="btn pri" data-m="close">${$t("Tamam")}</button></div>`, () => {});
    if (!parsed.candidates.length)
      return void openModal(`<h2>${$t("Sembol bulunamadı")}</h2><p>${$t("Dosyada blok tanımı ya da çizim yok.")}</p><div class="acts"><button class="btn pri" data-m="close">${$t("Tamam")}</button></div>`, () => {});
    const stem = files.length === 1 ? file.name.replace(/\.dxf$/i, '').slice(0, 40) : 'DXF sembolleri';
    const MAX = 400;
    const cands = parsed.candidates.slice(0, MAX);
    const opts = () => {
      const dlg = document.querySelector<HTMLElement>('.modal .dlg');
      // before the dialog exists (first preview render) use the defaults shown in it
      if (!dlg?.querySelector('#dxs')) return { scale: 'fit' as const, fitHeight: 16, cat: stem, prefix: 'U' };
      const sc = qs<HTMLSelectElement>(dlg, '#dxs').value;
      return {
        scale: sc === 'fit' ? ('fit' as const) : Number(sc) || 1,
        fitHeight: 16,
        cat: qs<HTMLInputElement>(dlg, '#dxc').value.trim() || stem,
        prefix: qs<HTMLInputElement>(dlg, '#dxp').value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4) || 'U',
      };
    };
    const grid = () =>
      cands
        .map((c, i) => {
          const sym = candidateToSymbol(c, opts());
          return `<label class="dxc"><input type="checkbox" data-dx="${i}"${cands.length <= 150 ? ' checked' : ''}>${previewSvg(sym, 72, 54)}<span>${esc(c.name)}</span></label>`;
        })
        .join('');
    const close = openModal(
      `<h2>${$t("DXF'ten sembol içe aktar")}</h2>
      <p>${$t("<b>{0}</b> · {1} · birim: {2} · {3} aday{4}. Bloklar ayrı sembol, model alanındaki ayrı çizimler de ayrı aday olarak listelenir.", esc(file.name), esc(parsed.version), esc(parsed.units), parsed.candidates.length, parsed.candidates.length > MAX ? $t(" (ilk {0} tanesi gösteriliyor)", MAX) : '')}</p>
      ${parsed.warnings.length ? `<p class="note">${parsed.warnings.map(esc).join(' ')}</p>` : ''}
      <div class="row2"><label class="fld"><span>${$t("Ölçek")}</span><select id="dxs"><option value="fit">${$t("Otomatik (yükseklik 16 mm)")}</option><option value="1">${$t("1 birim = 1 mm")}</option><option value="0.1">${$t("1 birim = 0,1 mm")}</option><option value="10">${$t("1 birim = 10 mm")}</option><option value="25.4">${$t("1 birim = 1 inç")}</option></select></label>
      <label class="fld"><span>${$t("Kategori")}</span><input id="dxc" value="${esc(stem)}"></label></div>
      <div class="row2"><label class="fld"><span>${$t("Etiket öneki")}</span><input id="dxp" class="tag" value="U" maxlength="4"></label>
      <div class="btnrow" style="align-self:end;margin-bottom:9px"><button class="btn" data-m="all">${$t("Tümünü seç")}</button><button class="btn" data-m="none">${$t("Hiçbiri")}</button></div></div>
      <div class="dxgrid" id="dxg">${grid()}</div>
      <p class="note">${$t("İçe aktarılan sembollerde bağlantı noktası yoktur; her sembolü açıp \"Bağlantı noktası\" aracıyla tellerin bağlanacağı yerleri ekleyin.")}</p>
      <div class="err" id="dxerr"></div>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn" data-m="edit">${$t("Seçileni düzenlemeye aç")}</button><button class="btn pri" data-m="save">${$t("Seçilenleri kaydet")}</button></div>`,
      async (a, dlg) => {
        if (a === 'all' || a === 'none') return dlg.querySelectorAll<HTMLInputElement>('[data-dx]').forEach((x) => (x.checked = a === 'all'));
        const chosen = [...dlg.querySelectorAll<HTMLInputElement>('[data-dx]:checked')].map((x) => cands[+x.dataset.dx!]);
        if (!chosen.length) return void (qs(dlg, '#dxerr').textContent = $t("En az bir aday seçin."));
        if (a === 'edit') {
          if (chosen.length > 1) return void (qs(dlg, '#dxerr').textContent = $t("Düzenlemeye açmak için tek aday seçin."));
          d = candidateToSymbol(chosen[0], opts());
          hist.length = 0;
          sel = null;
          close();
          render();
          return toast($t("Bağlantı noktalarını ekleyip Kaydet deyin"));
        }
        if (a === 'save') {
          const btn = dlg.querySelector<HTMLButtonElement>('[data-m="save"]')!;
          btn.disabled = true;
          let ok = 0;
          for (const c of chosen) {
            try {
              await api.saveSymbol(candidateToSymbol(c, opts()));
              ok++;
              btn.textContent = $t("Kaydediliyor… {0}/{1}", ok, chosen.length);
            } catch (x) {
              qs(dlg, '#dxerr').textContent = `${c.name}: ${(x as Error).message}`;
            }
          }
          await reload(true);
          await loadList();
          close();
          toast($t("{0} sembol kaydedildi; bağlantı noktalarını eklemeyi unutmayın", ok));
        }
      },
    );
    const dlg = document.querySelector<HTMLElement>('.modal .dlg')!;
    dlg.classList.add('wide');
    dlg.addEventListener('change', (e) => {
      const id = (e.target as HTMLElement).id;
      if (id === 'dxs' || id === 'dxp') {
        const checked = new Set([...dlg.querySelectorAll<HTMLInputElement>('[data-dx]:checked')].map((x) => x.dataset.dx));
        qs(dlg, '#dxg').innerHTML = grid();
        dlg.querySelectorAll<HTMLInputElement>('[data-dx]').forEach((x) => (x.checked = checked.has(x.dataset.dx)));
      }
    });
  };

  void me;
  await loadList();
  render();
}
